import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { PageHeader } from "@/components/landing/PageHeader";
import { API, authHeaders, useAmbassador } from "@/components/ambassadors/ambassadorApi";
import { LinkCard, StatCards } from "@/components/ambassadors/DashboardCards";
import { SalesTable, PayoutsList } from "@/components/ambassadors/SalesTable";
import { ProfileForm, SupportForm } from "@/components/ambassadors/ProfileSupport";

const TABS = [["sales", "Sales & earnings"], ["profile", "Profile & PayPal"], ["support", "Support"]];

export default function AmbassadorDashboard() {
  const { amb, setAmb, signOut } = useAmbassador();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("sales");

  useEffect(() => {
    if (!amb) return;
    axios.get(`${API}/ambassadors/me/sales`, { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => setData({ summary: {}, sales: [], payouts: [] }));
  }, [amb]);

  if (amb === null) return <Navigate to="/ambassadors/login" replace />;
  if (amb === undefined) return <main className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></main>;

  const first = amb.name.split(" ")[0];
  return (
    <main data-testid="ambassador-dashboard" className="bg-[#ffffff] text-[#0a0a0b] min-h-screen antialiased">
      <PageHeader page="ambassador-dashboard" link={{ id: "shop", to: "/shop", label: "Shop" }} />
      <section className="pt-28 sm:pt-36 pb-20 px-5 sm:px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-[#ff2a85] mb-4 flex items-center gap-3"><span className="diamond" /> Ambassador Crew</p>
              <h1 className="font-display font-extrabold uppercase leading-[0.9] text-4xl sm:text-5xl tracking-tight">Salaam, <span className="text-pink">{first}</span></h1>
              <p data-testid="amb-status" className="mt-3 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a]">Status · <span className={amb.status === "approved" ? "text-[#007a3d]" : "text-[#ff2a85]"}>{amb.status}</span> · code {amb.code}</p>
            </div>
            <button type="button" data-testid="amb-logout" onClick={signOut} className="h-11 px-5 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b]/40 text-[#5f5e5a] hover:text-[#0a0a0b] font-mono-d text-[11px] uppercase tracking-[0.2em] transition-colors flex items-center gap-2 self-start sm:self-auto"><LogOut size={14} /> Sign out</button>
          </div>

          <div className="mt-10 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5"><LinkCard amb={amb} /></div>
            <div className="lg:col-span-7">{data ? <StatCards summary={data.summary} amb={amb} /> : <div className="h-full min-h-[160px] flex items-center justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>}</div>
          </div>

          <div className="mt-12 flex gap-2 border-b border-[#e3e2dc] overflow-x-auto">
            {TABS.map(([id, label]) => (
              <button key={id} type="button" data-testid={`amb-tab-${id}`} onClick={() => setTab(id)} className={`px-5 py-3 font-mono-d text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === id ? "border-[#ff2a85] text-[#0a0a0b]" : "border-transparent text-[#5f5e5a] hover:text-[#0a0a0b]"}`}>{label}</button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "sales" && data && (
              <div className="space-y-8">
                <SalesTable sales={data.sales} />
                {data.payouts.length > 0 && <div><p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#5f5e5a] mb-3">Payouts</p><PayoutsList payouts={data.payouts} /></div>}
                <p className="text-xs text-[#5f5e5a] leading-relaxed max-w-2xl">Commission is {amb.rate_pct}% of the profit on each piece (sale price minus cost and card fees). It shows as pending when the order is paid, becomes available once the order ships, and is paid to your PayPal by the Deen District team. Refunded orders are removed. Questions? Use <button type="button" onClick={() => setTab("support")} className="underline underline-offset-4 hover:text-[#ff2a85]">Support</button>.</p>
              </div>
            )}
            {tab === "profile" && <div className="max-w-3xl"><ProfileForm amb={amb} onSaved={setAmb} /></div>}
            {tab === "support" && (
              <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4">
                  <h2 className="font-display font-extrabold uppercase text-xl leading-tight">We're here</h2>
                  <p className="mt-3 text-sm text-[#5f5e5a] leading-relaxed">Payout timing, link tracking, content you'd like from us — send it here and we'll reply to {amb.email}. You can also <Link to="/shop" className="underline underline-offset-4 hover:text-[#ff2a85]">browse the drop</Link> for pieces to feature.</p>
                </div>
                <div className="lg:col-span-8"><SupportForm /></div>
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
